import { LearningGoal } from '@/domain/models/onboarding/learning-goal'
import { PreferredContentFormat } from '@/domain/models/onboarding/preferred-content-format'
import {
  EMPTY_LEARNING_PREFERENCES,
  LearningPreferencesApiData,
  LearningPreferencesModel,
  LearningPreferencesPatchParams,
  PreferredClassType
} from './learning-preferences-service-types'

const parsePreferredClassType = (value: string | null): PreferredClassType | null =>
  Object.values(PreferredClassType).includes(value as PreferredClassType)
    ? (value as PreferredClassType)
    : null

/**
 * Converte o payload da API em modelo de domínio.
 *
 * Chave desconhecida vira `null` (ou é descartada da lista) em vez de quebrar a
 * tela: o vocabulário de `learningGoal` ainda não está fechado com o backend, e
 * um valor novo chegando antes do deploy do front não pode derrubar o Meu Espaço.
 */
export const parseLearningPreferences = (
  data: LearningPreferencesApiData | null | undefined
): LearningPreferencesModel => {
  if (!data) {
    return EMPTY_LEARNING_PREFERENCES
  }

  return {
    learningGoal: data.learningGoal ? LearningGoal.getByName(data.learningGoal) || null : null,
    // `??` e não `||`: 0 é valor declarado, não campo vazio
    weeklyDedication: data.weeklyDedication ?? null,
    preferredClassType: parsePreferredClassType(data.preferredClassType),
    contentFormats: (data.contentFormats ?? [])
      .map(key => PreferredContentFormat.getByKey(key))
      .filter((format): format is PreferredContentFormat.Enum => !!format)
  }
}

/**
 * Monta o corpo do PATCH preservando a semântica parcial do contrato.
 *
 * A distinção entre "ausente", `null` e `[]` é o coração do endpoint: cada card
 * da tela salva sozinho, então mandar o objeto inteiro faria um save sobrescrever
 * os campos que o usuário não tocou. `undefined` some do corpo; `null` e `[]`
 * permanecem porque significam "limpa".
 */
export const buildLearningPreferencesPatchBody = (
  params: LearningPreferencesPatchParams
): Record<string, unknown> => {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined))
}
